import classNames from 'classnames';
import { Permalink } from 'helpers';
import { useLang } from 'context/langContext';
import Analytics from 'helpers/Analytics';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { MutableRefObject, useEffect, useRef } from 'react';
import { HomepageSection } from 'typings/api/homepage';
import { Namespace } from '../../enums/namespaces';
import { useSwiper } from '../../hooks';
import AppLink from '../AppLink/AppLink';
import PlayButton from '../buttons/PlayButton/PlayButton';
import styles from './index.module.scss';
import { useMemo } from 'react';

interface Props {
  section: HomepageSection;
  linkToOriginals: string;
}

export const Carousel = ({ section, linkToOriginals }: Props): JSX.Element => {
  const ref: MutableRefObject<HTMLDivElement> = useRef(null);
  const progressBarRef = useRef(null);
  const router = useRouter();
  const { locale } = useLang();

  const { t } = useTranslation(Namespace.Common);

  const btnOriginal = useMemo(() => {
    return (
      <a href={linkToOriginals} className="original-link" title="Dramox Original">
        Dramox Original
      </a>
    );
  }, [linkToOriginals]);

  useEffect(() => {
    /*
    HACK
    If we lose focus on the window, carousel freezes on the current frame.
    When the window regains focus, carousel restarts the current frame but the active "spinning bullet" never re-appears.
    Hacky solution is to remove active class from the bullet and re-add it on the next render frame to start the animation again.
    */
    const handleVisibilityChange = () => {
      const cls = 'swiper-pagination-bullet-active';

      if (!document.hidden) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        const el = document.getElementsByClassName(cls)[0];
        el?.classList.remove(cls);
        setTimeout(() => {
          el?.classList.add(cls);
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, false);
    };
  }, []);

  useSwiper(ref, progressBarRef);

  return (
    <>
      <div ref={ref} className={classNames('carousel-wrapper', styles.carousel)}>
        <ul className="dramox-carousel swiper-wrapper">
          {section?.plays?.map((item) => (
            <li key={item.id} className="swiper-slide">
              <span className="thumb" style={{ backgroundImage: `url(${item.poster_url})` }} />
              <div className="content">
                <div className="links-wrapper">
                  <a href={Permalink.theatre(item.theatre)} className="theatre-link">
                    {item.theatre.name}
                  </a>
                  {item.is_original && btnOriginal}
                </div>
                {item.is_original && btnOriginal}
                <h3>
                  {item.logo_url && <img src={item.logo_url} alt={item.title} />}
                  <span>{item.title}</span>
                </h3>
                <p>{item.summary}</p>
                <div className="buttons">
                  <PlayButton play={item} alwaysOn />
                  <AppLink href={Permalink.play(item, locale)}>
                    <a
                      className="btn btn-outline-info"
                      tabIndex={-1}
                      onMouseUp={() => {
                        router.push(Permalink.play(item, locale));
                      }}
                    >
                      {t('more_about_play')}
                    </a>
                  </AppLink>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="swiper-pagination" />
      </div>
    </>
  );
};
